import styles from '../ManageUserTable/ManageUserTable.module.scss';
import { Headers } from '../useManageUser';
import loaderSvg from '../../../../assets/aica-loader.svg';
import clsx from 'clsx';
import { ManageUserDrawer } from '../../../Company/UploadData/components/ManageUserDrawer';
import { useOrgBasedUsersTable } from './useOrgBasedUsersTable';
import { debtProductsEmpty } from '../../../../assets/hostedassets';
import ManageDealDrawer from '../../../../common/ManageDealDrawer/ManageDealDrawer';
import { COMMENTS_THREAD_SOURCES } from '../../../../store/commentsThread/commentsThread_reducer';
import { USER_TYPE } from '../../../../utils/constants/user';
import { useEffect, useState } from 'react';
import { getDealFormConfig } from '../../../../store/investor/apiHelpers';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { VIEW_CONFIGS } from '../../../../utils/constants/commonConstants';
import { DataTableColumn, SingleSelectDropdownCellRenderer } from '../../../../common/_custom';
import DataTable from '../../../../common/_custom/DataTable/DataTable';
import { DataTableColumnType } from '../../../../common/_custom/DataTable/DataTable.types';
import { PillCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/PillCellRenderer/PillCellRenderer';
import { OwnerCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/OwnerCellRenderer';
import { CommentCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/CommentCellRenderer';
import { TextCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/TextCellRenderer';
import { ComponentCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/ComponentCellRenderer/ComponentCellRenderer';
import { NumberCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/NumberCellRenderer/NumberCellRenderer';
import { DatePickerCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/DatePickerCellRenderer/DatePickerCellRenderer';
import { CompanyNameCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/CompanyNameCellRenderer/CompanyNameCellRenderer';
import ActionMenu from '../../../../common/ActionMenu/ActionMenu';
import { UpdateSingleSelectDropdownProps } from '../../../../common/_custom/DataTable/components/CellRenderers/SingleSelectDropdownCellRenderer';
import { updateCompany } from '../../../../store/investor/action';
import { getPlatformUserId, openUserSummonTab } from '../../../../utils/utils';
import { fetchInternalSummonAicaData, InternalSummonAicaDataPayload } from '../../../../store/user/actions';

export type OrgUser = {
    userId: string;
    emailId: string;
    name: string;
    mobileNumber: string;
    userType: string;
    organizationId: string;
    phoneVerified: boolean;
    emailVerified: boolean;
    countryCode: string;
};

export type OrgBasedUser = {
    organizationName: string;
    organizationDisplayName: string;
    organizationType: string;
    userData: OrgUser[];
    organizationOwner: string;
    organizationId: string;
    comment?: { text: string; activityDate: Date; activityBy: string };
    investmentPreferencesCount: number;
    orgAdditionalDetails: {
        feeAligned: string;
    };
};

export type OrgBasedUsersTableProps = {
    headers: Headers;
    rows: any;
    setRows: (rows: any) => void;
    isRowsLoading: boolean;
    isSummonUser: boolean;
    actionMenuItems: Record<string, { action: (payload: any, source: string) => void }>;
    category: 'Partner' | 'Lender';
    actionConfig: string[];
    fetchRows: (pageNo: number) => Promise<void>;
    onFiltersApply: (filters: Record<string, any>) => void;
};

const OrgBasedUsersTable = ({
    headers,
    rows,
    setRows,
    isRowsLoading,
    isSummonUser,
    actionMenuItems,
    category,
    actionConfig,
    fetchRows,
    onFiltersApply,
}: OrgBasedUsersTableProps) => {
    const {
        manageUserDrawerState,
        selectedOrgUsers,
        selectedOrgName,
        originalCategory,
        showDealDrawer,
        dealDrawerState,
        handleDrawerClose,
        getRowText,
        handleDealDrawerClose,
        handleManageUsers,
        commentsThreadState,
        updateOwner,
        localOwners,
        getSelectedOption,
        handleRowClick,
        handleUsersUpdate,
    } = useOrgBasedUsersTable({
        headers,
        rows,
        setRows,
        isRowsLoading,
        isSummonUser,
        actionMenuItems,
        category,
        actionConfig,
        fetchRows,
        onFiltersApply,
    });
    const dispatch = useAppDispatch();
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const owners = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const [managePartnersColumn, setManagePartnersColumn] = useState<{
        columns: DataTableColumn[];
        pinnedColumns?: { field: string; position: 'left' | 'right' }[];
    }>({ columns: [] });
    const [manageLendersColumn, setManageLendersColumn] = useState<{
        columns: DataTableColumn[];
        pinnedColumns?: { field: string; position: 'left' | 'right' }[];
    }>({ columns: [] });

    const config = category === 'Lender' ? manageLendersColumn : managePartnersColumn;
    const mapColumnsType = (columns: any[]): any[] =>
        columns.map((col) => ({
            ...col,
            type: DataTableColumnType[col.type as keyof typeof DataTableColumnType] || col.type,
        }));

    const columns = mapColumnsType(config.columns || []).map((col, index) => {
        if (index === 0 && (col.field === 'organizationDisplayName' || col.field === 'organizationName')) {
            return {
                ...col,
                type: DataTableColumnType.COMPANY_NAME_CTA,
            };
        }
        return col;
    });

    const pinnedColumns =
        'pinnedColumns' in config && Array.isArray((config as any).pinnedColumns) ? (config as any).pinnedColumns : undefined;

    const componentRegistry = ({ row, column, actionMenuItems, handleManageUsers, isSummonUser }) => {
        if (column.componentName === 'ActionMenu') {
            return (
                <div>
                    <ActionMenu
                        triggerPayload={{
                            organizationId: row?.organizationId,
                            organizationName: row?.organizationName,
                            selectedUsers: row?.userData,
                            userId: row?.userData?.[0]?.userId,
                            userType: row?.userData?.[0]?.userType,
                            organizationDisplayName: row?.organizationDisplayName,
                        }}
                        rowData={{
                            mainConfig: {
                                actionConfig,
                            },
                        }}
                        actionMenu={{
                            ...actionMenuItems,
                            'Manage User': {
                                action: (payload) => {
                                    const heading = payload?.organizationDisplayName || payload?.organizationName;
                                    handleManageUsers(payload?.selectedUsers, payload?.organizationId, heading);
                                },
                            },
                        }}
                        source="MANAGE_USERS"
                        disabled={isSummonUser}
                        usePortal={true}
                    />
                </div>
            );
        }
        return null;
    };

    const updateSingleSelectDropdown = ({ row, option, column, handlers }: UpdateSingleSelectDropdownProps) => {
        const field = column.field;
        const value = option?.value;
        const orgId = row.organizationId;

        updateLocalSingleSelectDropdown(field, value, orgId);

        if (field.includes('customFields')) {
            const customFieldKey = field.split('.')[1];
            // Start with the selected custom field value
            const customFields = {
                [customFieldKey]: value,
            };
            // Merge in any config-driven onChangeUpdateFields, but never overwrite the user's selection
            if (column.onChangeUpdateFields) {
                Object.entries(column.onChangeUpdateFields).forEach(([k, v]) => {
                    if (k.startsWith('customFields.')) {
                        const fieldKey = k.split('.')[1];
                        if (fieldKey !== customFieldKey) {
                            customFields[fieldKey] = v;
                        }
                    }
                });
            }
            dispatch(
                updateCompany(
                    {
                        orgId,
                        customFields,
                    },
                    handlers,
                ),
            );
            return;
        }
        dispatch(
            updateCompany(
                {
                    orgId,
                    [field]: value,
                },
                handlers,
            ),
        );
    };

    const updateLocalSingleSelectDropdown = (field, value, orgId) => {
        setRows((prev) => {
            const newRows = prev.map((row) => {
                if (row.organizationId === orgId) {
                    return { ...row, [field]: value };
                }
                return row;
            });
            return newRows;
        });
    };

    const updateCustomFieldStatus = (row, option, column, handlers) => {
        const field = column.field;
        const orgId = row.organizationId;
        const value = option?.value !== undefined ? option.value : option;

        setRows((prev) => {
            const newRows = prev.map((r) => {
                if (r.organizationId !== orgId) {
                    return r;
                }
                if (field.includes('customFields')) {
                    return {
                        ...r,
                        customFields: {
                            ...r.customFields,
                            [field.split('.')[1]]: value,
                        },
                    };
                }
                return { ...r, [field]: value };
            });
            return newRows;
        });

        // Call API to update the backend
        if (field.includes('customFields')) {
            const customFieldKey = field.split('.')[1];
            dispatch(
                updateCompany(
                    {
                        orgId,
                        customFields: {
                            [customFieldKey]: value,
                        },
                    },
                    handlers,
                ),
            );
            return;
        }

        dispatch(
            updateCompany(
                {
                    orgId,
                    [field]: value,
                },
                handlers,
            ),
        );
    };

    const summonInternalUser = (payload: any) => {
        const platformUserId = getPlatformUserId();
        const summonPayload: InternalSummonAicaDataPayload = {
            parentOrgId: userData?.investorOrgId as string,
            parentEmailId: userData.investorPrimaryEmail,
            userId: payload?.userId,
            userType: payload?.userType,
            requesterUserId: platformUserId,
        };
        dispatch(
            fetchInternalSummonAicaData(summonPayload, {
                onSuccess: (userData) => {
                    openUserSummonTab({ ...userData, orgId: platformData?.investorOrgId });
                },
            }),
        );
    };

    // Custom cell renderers
    const customCellRenderers = {
        [DataTableColumnType.TEXT]: (props) => <TextCellRenderer {...props} />,
        [DataTableColumnType.COMPANY_NAME_CTA]: (props) => {
            return (
                <CompanyNameCellRenderer
                    {...props}
                    dynamicProps={{
                        handleRowClick: ({ event, row }) => {
                            event?.stopPropagation();
                            event?.preventDefault();
                            summonInternalUser({
                                userId: row?.userData?.[0]?.userId,
                                userType: row?.userData?.[0]?.userType,
                            });
                        },
                    }}
                />
            );
        },
        [DataTableColumnType.NUMBER]: (props) => (
            <NumberCellRenderer
                {...props}
                dynamicProps={{
                    updateCustomFieldStatus,
                    isDisabled:
                        isSummonUser || !props?.row?.organizationId || (props?.column?.disable && !props?.row?._isCollapsibleRow) || false,
                }}
            />
        ),
        [DataTableColumnType.OWNER]: (props) => (
            <OwnerCellRenderer
                {...props}
                dynamicProps={{
                    getSelectedOption,
                    updateOwner: (payload) => {
                        if (payload && payload.row && payload.value !== undefined) {
                            // Find the owner object to get customSortName
                            const selectedOwner = owners.find((owner) => owner.userId === payload.value);
                            const ownerName = selectedOwner?.customSortName || selectedOwner?.name || payload.label;

                            updateOwner(payload.row.organizationId, {
                                name: ownerName,
                                userId: payload.value,
                                activityBy: userData.userName,
                                activityDate: new Date().toISOString(),
                                ownerType: 'owner',
                            });
                        }
                    },
                    isDisabled: isSummonUser,
                }}
            />
        ),
        [DataTableColumnType.COMMENT]: (props) => (
            <CommentCellRenderer
                {...props}
                dynamicProps={{
                    getOrgId: (row) => row.organizationId,
                    source: 'MANAGE_PARTNERS_LENDERS',
                    isDealView: false,
                    borrowerName: (row) => row.organizationName,
                }}
            />
        ),
        [DataTableColumnType.PILL]: ({ value }) => {
            const isActive = value && value > 0;
            const label = isActive ? `${value} active` : 'Not active';
            return <PillCellRenderer label={label} isActive={isActive} />;
        },
        [DataTableColumnType.COMPONENT]: (props) => (
            <ComponentCellRenderer
                {...props}
                dynamicProps={{
                    componentRenderFnRegistry: ({ row, column }) =>
                        componentRegistry({ row, column, actionMenuItems, handleManageUsers, isSummonUser }),
                }}
            />
        ),
        [DataTableColumnType.DATE_PICKER]: (props) => (
            <DatePickerCellRenderer
                {...props}
                dynamicProps={{
                    updateCustomFieldStatus,
                    isDisabled: isSummonUser || !props?.row?.organizationId || false,
                }}
            />
        ),
        [DataTableColumnType.SINGLE_SELECT_DROPDOWN]: (props) => (
            <SingleSelectDropdownCellRenderer
                {...props}
                dynamicProps={{
                    updateSingleSelectDropdown,
                }}
            />
        ),
    };

    const fetchConfig = async () => {
        try {
            await getDealFormConfig(
                {
                    accountType: userData.accountType,
                    investorOrgId: platformData?.investorOrgId,
                    primaryEmail: userData?.primaryEmail,
                    configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
                },
                {
                    onSuccess: (configData, orgName) => {
                        setManageLendersColumn(configData?.manageLendersColumns ?? { columns: [] });
                        setManagePartnersColumn(configData?.managePartnersColumns ?? { columns: [] });
                    },
                },
            );
        } catch (error) {
            console.error('Error fetching leads columns config:', error);
        }
    };

    useEffect(() => {
        if (category) {
            fetchConfig();
        }
    }, [category, platformData]);

    return (
        <div className={styles.TableContainer}>
            {manageUserDrawerState && (
                <ManageUserDrawer
                    isOrgDrawer={true}
                    onClose={handleDrawerClose}
                    selectedOrgName={selectedOrgName}
                    selectedUsers={selectedOrgUsers}
                    parentCategory={originalCategory}
                    onUsersUpdate={handleUsersUpdate}
                />
            )}
            <DataTable
                columns={columns}
                rows={rows}
                loading={isRowsLoading}
                customCellRenderers={customCellRenderers}
                pinnedColumns={pinnedColumns}
                totalRowCount={Array.isArray(rows) ? rows.length : 0}
                emptyState={<div>No data found</div>}
                noSearchResults={<div>No results found</div>}
                valueGetterMap={{
                    userData: (row) => (Array.isArray(row.userData) && row.userData.length > 0 ? row.userData.map((u) => u.name) : []),
                    comment: (row) => row.comment || null,
                }}
                onRowClick={(e, row) => {
                    e.preventDefault?.();
                    e.stopPropagation?.();

                    const clickedCell = (e.target as HTMLElement).closest('td');
                    if (!clickedCell) return;

                    const cellIndex = Array.from(clickedCell.parentNode?.children || []).indexOf(clickedCell);
                    const column = columns[cellIndex];

                    if (column) handleRowClick(column, row, e);
                }}
                filterSourceKey={category === 'Lender' ? 'MANAGE_LENDERS' : 'MANAGE_PARTNERS'}
                onFiltersApply={onFiltersApply}
            />
            {showDealDrawer && dealDrawerState.drawerSource !== 'ProfileButton' && (
                <ManageDealDrawer source={'OrgUsersTable'} onClose={handleDealDrawerClose} />
            )}
        </div>
    );
};

export default OrgBasedUsersTable;
